import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePurchases } from '../../contexts/PurchasesContext';
import { FontText, SEOHead, Breadcrumbs } from '../../components/common';
import { Header } from '../../components/Header';
import { localAnalytics } from '../../utils/analytics';
import { Link } from 'react-router-dom';

interface Hub {
  title: string;
  description: string;
  url: string;
  emoji: string;
}

export const Hubs: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { userId, isAuthenticated, isCampaign, isAnonymous } = useAuth();
  const { isUserPremium, coinBalance } = usePurchases();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    localAnalytics().logEvent('PageLoaded', {
      screen: 'Hubs',
      page: 'Tropes',
      userId,
      isAuthenticated,
      isPremium: isUserPremium,
      isCampaign,
      isAnonymous,
      coinBalance,
    });
  }, [
    userId,
    isAuthenticated,
    isUserPremium,
    isCampaign,
    isAnonymous,
    coinBalance,
  ]);

  const hubs: Hub[] = [
    {
      title: 'Billionaire Romance',
      description:
        'Definition, top tropes, and the newest billionaire romance releases for 2025',
      url: '/hub/billionaire-romance',
      emoji: '💎',
    },
    {
      title: 'Best Billionaire Romance Books',
      description:
        'Jet-set passion, ruthless alphas, and undeniable chemistry: discover the best billionaire romance books of 2025',
      url: '/hub/best-billionaire-romance-books',
      emoji: '⭐',
    },
    {
      title: 'Read Billionaire Romance Online Free',
      description:
        'Where to read billionaire romance online free in 2025—alpha CEOs, dark tycoons, and brand‑new episodic love stories',
      url: '/hub/billionaire-romance-read-online',
      emoji: '📱',
    },
    {
      title: 'Boss & CEO Billionaire Romance',
      description:
        'Read boss & CEO billionaire romance books online—dominant CEOs, fake-fiancée contracts, and forbidden office affairs',
      url: '/hub/boss-ceo-romance',
      emoji: '💼',
    },
  ];

  const handleHubClick = (hub: Hub) => {
    localAnalytics().logEvent('TropeHubClicked', {
      screen: 'Hubs',
      action: 'HubClicked',
      userId,
      hubTitle: hub.title,
      hubUrl: hub.url,
      isAuthenticated,
      isPremium: isUserPremium,
    });
  };

  const handleBreadcrumbClick = (breadcrumb: {
    name: string;
    url?: string;
  }) => {
    localAnalytics().logEvent('BreadcrumbClicked', {
      screen: 'Hubs',
      action: 'BreadcrumbClicked',
      userId,
      breadcrumbName: breadcrumb.name,
      breadcrumbUrl: breadcrumb.url,
      isAuthenticated,
      isPremium: isUserPremium,
    });
  };

  const handleMenuToggle = () => {
    const newMenuState = !menuOpen;
    setMenuOpen(newMenuState);

    localAnalytics().logEvent('MenuToggled', {
      screen: 'Hubs',
      action: 'MenuToggled',
      userId,
      menuOpen: newMenuState,
      isAuthenticated,
      isPremium: isUserPremium,
    });
  };

  const breadcrumbItems = [{ name: 'Home', url: '/' }, { name: 'Tropes' }];

  const isLargeScreen = window.innerWidth > 900;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
    },
    main: {
      padding: '20px 30px',
      maxWidth: 1200,
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: 40,
    },
    title: {
      color: theme.colors.text,
      marginBottom: 16,
    },
    subtitle: {
      color: theme.colors.textSecondary,
      lineHeight: '1.6',
      maxWidth: 600,
      margin: '0 auto',
    },
    hubsGrid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fit, minmax(${isLargeScreen ? '300px' : '280px'}, 1fr))`,
      gap: 24,
      marginTop: 40,
    },
    hubCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: 16,
      padding: 24,
      border: `1px solid ${theme.colors.overlay}`,
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      textDecoration: 'none',
    },
    hubCardHover: {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
    },
    hubEmoji: {
      fontSize: '2.5rem',
      marginBottom: 16,
      display: 'block',
    },
    hubTitle: {
      color: theme.colors.text,
      marginBottom: 12,
    },
    hubDescription: {
      color: theme.colors.textSecondary,
      lineHeight: '1.5',
    },
  };

  return (
    <>
      <SEOHead
        title="Billionaire Romance Tropes & Categories | NanoReads"
        description="Explore billionaire romance tropes and categories - from CEO office affairs to dark tycoon secrets. Find your perfect billionaire love story."
        canonical="/hub"
        ogImage="/img/favicon.png"
        ogType="website"
      />
      <div style={styles.container}>
        <Header
          isPremium={isUserPremium}
          isAuthenticated={isAuthenticated}
          isCampaign={isCampaign}
          isAnonymous={isAnonymous}
          userCoins={coinBalance}
          menuOpen={menuOpen}
          onMenuToggle={handleMenuToggle}
        />

        <main style={styles.main}>
          <Breadcrumbs
            items={breadcrumbItems}
            onBreadcrumbClick={handleBreadcrumbClick}
          />

          <div style={styles.header}>
            <FontText h1 style={styles.title}>
              {t('billionaire_romance_tropes')}
            </FontText>
            <FontText normal style={styles.subtitle}>
              {t('explore_billionaire_romance_categories')}
            </FontText>
          </div>

          <div style={styles.hubsGrid}>
            {hubs.map((hub, index) => (
              <Link
                key={index}
                to={hub.url}
                style={styles.hubCard}
                onClick={() => handleHubClick(hub)}
                onMouseEnter={e => {
                  Object.assign(e.currentTarget.style, styles.hubCardHover);
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow =
                    '0 4px 20px rgba(0, 0, 0, 0.1)';
                }}
              >
                <span style={styles.hubEmoji}>{hub.emoji}</span>
                <FontText h3 style={styles.hubTitle}>
                  {hub.title}
                </FontText>
                <FontText normal style={styles.hubDescription}>
                  {hub.description}
                </FontText>
              </Link>
            ))}
          </div>
        </main>
      </div>
    </>
  );
};
